// BorrowingCapacityStep.js — Mortgage Matrix Broker CRM
// v5.9.1 — Live Product APIs (ANZ/Westpac/CBA) + BID: lock 3 options + corrected bank logo file paths
// ---------------------------------------------------------------------------------------------------------------
// Notes:
// - Product Reference Data (CDR) endpoints are generally public (no API keys). In-browser calls may still fail due to CORS.
//   Production best practice: proxy + cache via Lambda/CloudFront.
// - Logos: uses PUBLIC/static paths matching actual files in /public/bank-logos
// - BID: lock up to 3 options + mark 1 as Recommended, and write this summary to Mercury Notepad via onWriteMercuryNotepad()

import React, { useEffect, useMemo, useState } from "react"
import CalculateNetIncome from "../../../helpers/calculateNetIncome"
import HemDependant from "../../../helpers/hemDependant"
import HemSingleJoint from "../../../helpers/hemSingleJoint"
import CalculatePV from "../../../helpers/calculatePV"

// ----------------------------
// Formatters
// ----------------------------
const Money0 = new Intl.NumberFormat("en-AU", {
  style: "currency",
  currency: "AUD",
  maximumFractionDigits: 0,
  minimumFractionDigits: 0,
})

const Money2 = new Intl.NumberFormat("en-AU", {
  style: "currency",
  currency: "AUD",
  maximumFractionDigits: 2,
  minimumFractionDigits: 2,
})

// ----------------------------
// Helpers
// ----------------------------
const parseAmount = (val) => {
  if (val === null || val === undefined) return 0
  const n = Number(String(val).replace(/[^\d.-]/g, ""))
  return Number.isFinite(n) ? n : 0
}

const clamp = (n, min, max) => Math.max(min, Math.min(max, Number(n || 0)))
const pct = (n) => `${Number(n || 0).toFixed(2)}%`
const safeLower = (v) => String(v || "").trim().toLowerCase()

const annualise = (amount, frequency) => {
  const v = parseAmount(amount)
  switch (frequency) {
    case "Weekly":
      return v * 52
    case "Fortnightly":
      return v * 26
    case "Monthly":
      return v * 12
    default:
      return v
  }
}

const getAnnualIncomeForOwner = (items, ownerLabel) =>
  items
    .filter((i) => (i.owner || "Applicant 1") === ownerLabel && (i.category || "") !== "Rental")
    .reduce((sum, i) => sum + annualise(i.amount, i.frequency || "Annual"), 0)

const getMonthlyRentalIncome = (items) =>
  items
    .filter((i) => (i.category || "") === "Rental")
    .reduce((sum, i) => sum + annualise(i.amount, i.frequency || "Monthly") / 12, 0)

const getLiabilitySummaries = (data) => {
  const liabs = Array.isArray(data?.liabilities) ? data.liabilities : []

  let creditCardLimit = 0
  let personalLoanRepay = 0
  let homeLoanRepay = 0

  liabs.forEach((l) => {
    const type = l.type || ""
    const limit = parseAmount(l.limit)
    const repayment = parseAmount(l.repayment)

    if (type === "Credit Card") creditCardLimit += limit
    else if (type === "Personal Loan" || type === "Car Loan") personalLoanRepay += repayment
    else if (type === "Home Loan") homeLoanRepay += repayment
  })

  return { creditCardLimit, personalLoanRepay, homeLoanRepay }
}

const sumHomeLoanBalances = (data) => {
  const liabs = Array.isArray(data?.liabilities) ? data.liabilities : []
  return liabs
    .filter((l) => (l.type || "") === "Home Loan")
    .reduce((sum, l) => {
      const bal = parseAmount(l.balance ?? l.owing ?? l.amount ?? l.limit ?? 0)
      return sum + bal
    }, 0)
}

const getTxnType = (crmData) => {
  const fromMeta =
    crmData?.metadata?.desiredTransactionType ||
    crmData?.metadata?.transactionType ||
    crmData?.metadata?.applicationType

  const fromTop =
    crmData?.desiredTransactionType || crmData?.transactionType || crmData?.applicationType

  const v = safeLower(fromMeta || fromTop)
  if (v.includes("purchase") || v.includes("buy")) return "purchase"
  if (v.includes("refinance") || v.includes("refi")) return "refinance"
  return ""
}

const getPurchasePrice = (crmData) => {
  return (
    parseAmount(crmData?.purchase?.purchasePrice) ||
    parseAmount(crmData?.purchase?.price) ||
    parseAmount(crmData?.purchaseDetails?.purchasePrice) ||
    parseAmount(crmData?.purchaseDetails?.price) ||
    parseAmount(crmData?.property?.purchasePrice) ||
    parseAmount(crmData?.property?.price) ||
    0
  )
}

const getDepositAmount = (crmData) => {
  const direct =
    parseAmount(crmData?.purchase?.depositAmount) ||
    parseAmount(crmData?.purchaseDetails?.depositAmount) ||
    parseAmount(crmData?.property?.depositAmount) ||
    0

  if (direct > 0) return direct

  const assets = Array.isArray(crmData?.assetsLiabilities?.assets) ? crmData.assetsLiabilities.assets : []
  const depositAsset = assets.find((a) => (a.metaKey || a.key || "") === "deposit_savings")
  if (!depositAsset) return 0
  return parseAmount(depositAsset.value ?? depositAsset.amount ?? 0)
}

const getPropertyValue = (crmData) => {
  return (
    parseAmount(crmData?.property?.estimatedValue) ||
    parseAmount(crmData?.property?.propertyValue) ||
    parseAmount(crmData?.property?.value) ||
    parseAmount(crmData?.security?.estimatedValue) ||
    parseAmount(crmData?.security?.value) ||
    0
  )
}

const getLvrTierLabel = (lvr) => {
  const v = Number(lvr || 0)
  if (v <= 0) return ""
  if (v < 70) return "Under 70% tier"
  if (v <= 80) return "70–80% tier"
  if (v <= 90) return "80–90% tier"
  return "90–95% tier"
}

const getPersistKey = (crmData) => {
  const meta = crmData?.metadata || {}
  const id =
    meta.mercuryOpportunityId ||
    meta.opportunityId ||
    meta.mercuryContactId ||
    meta.contactId ||
    crmData?.opportunityId ||
    "unknown"
  return `mm_chooseLoan_lock_${String(id)}`
}

// ----------------------------
// Repayment estimate (monthly, P&I, 30 years)
// ----------------------------
const calcMonthlyRepayment = ({ principal, annualRatePct, termYears = 30 }) => {
  const P = Number(principal || 0)
  const rPct = Number(annualRatePct || 0)
  const n = Math.round(Number(termYears || 30) * 12)
  if (!(P > 0) || !(rPct > 0) || !(n > 0)) return 0

  const r = (rPct / 100) / 12
  if (r === 0) return P / n

  const pow = Math.pow(1 + r, n)
  const M = (P * r * pow) / (pow - 1)
  return Number.isFinite(M) ? M : 0
}

// ----------------------------
// Rate selection model
// ----------------------------
const RATE_TYPE = [
  { key: "ALL", label: "All" },
  { key: "VARIABLE", label: "Variable" },
  { key: "FIXED", label: "Fixed" },
]

const FIXED_TERMS = [
  { key: "ANY", label: "Any term" },
  { key: "Fixed 1yr", label: "Fixed 1yr" },
  { key: "Fixed 2yrs", label: "Fixed 2yrs" },
  { key: "Fixed 3yrs", label: "Fixed 3yrs" },
  { key: "Fixed 4yrs", label: "Fixed 4yrs" },
  { key: "Fixed 5yrs", label: "Fixed 5yrs" },
]

// ----------------------------
// CDR helpers (ANZ / Westpac / CBA)
// ----------------------------
const looksOwnerOccupied = (loanPurpose) => {
  const v = safeLower(loanPurpose)
  return v.includes("owner") || v.includes("principal") || v.includes("home") || v.includes("residential")
}

const looksInvestment = (loanPurpose) => {
  const v = safeLower(loanPurpose)
  return v.includes("invest") || v.includes("rental")
}

const looksFixed = (rateType) => safeLower(rateType).includes("fixed")
const looksVariable = (rateType) => safeLower(rateType).includes("variable") || safeLower(rateType).includes("var")

const tierMaxPercent = (tier) => {
  const u = safeLower(tier?.unitOfMeasure)
  if (!u.includes("percent")) return null
  const max = tier?.maximumValue
  const min = tier?.minimumValue
  const maxN = Number(max)
  const minN = Number(min)
  if (Number.isFinite(maxN)) return maxN
  if (Number.isFinite(minN)) return minN
  return null
}

const pickTierForLvr = (tiers, lvr) => {
  const lv = Number(lvr || 0)
  const arr = Array.isArray(tiers) ? tiers : []
  const candidates = arr
    .map((t) => ({
      t,
      u: safeLower(t?.unitOfMeasure),
      min: Number(t?.minimumValue),
      max: Number(t?.maximumValue),
      rate: Number(t?.rate),
    }))
    .filter((x) => x.u.includes("percent") && Number.isFinite(x.rate))

  const inRange = candidates.find((x) => {
    const hasMin = Number.isFinite(x.min)
    const hasMax = Number.isFinite(x.max)
    if (hasMin && hasMax) return lv >= x.min && lv <= x.max
    if (hasMax) return lv <= x.max
    if (hasMin) return lv >= x.min
    return false
  })
  if (inRange) return inRange.t

  const byMax = candidates
    .filter((x) => Number.isFinite(x.max))
    .sort((a, b) => a.max - b.max)
    .find((x) => lv <= x.max)
  if (byMax) return byMax.t

  const lowest = candidates.sort((a, b) => a.rate - b.rate)[0]
  return lowest?.t || null
}

const pickCdrLendingRate = ({ lendingRates, occupancy, lvr, wantFixed }) => {
  const arr = Array.isArray(lendingRates) ? lendingRates : []
  if (arr.length === 0) return null

  const occ = occupancy === "investment" ? "investment" : "ownerOccupied"
  const occMatches = arr.filter((lr) => {
    const lp = lr?.loanPurpose || lr?.lendingRateType || lr?.rateType || ""
    if (occ === "investment") return looksInvestment(lp)
    return looksOwnerOccupied(lp) || !looksInvestment(lp)
  })

  const pool = occMatches.length > 0 ? occMatches : arr

  const rateTypeMatches = pool.filter((lr) => {
    const rt = lr?.rateType || lr?.lendingRateType || ""
    if (!rt) return true
    return wantFixed ? looksFixed(rt) : looksVariable(rt)
  })

  const pool2 = rateTypeMatches.length > 0 ? rateTypeMatches : pool

  let best = null
  let bestRate = null

  pool2.forEach((lr) => {
    const tiers = Array.isArray(lr?.tiers) ? lr.tiers : []
    const tier = pickTierForLvr(tiers, lvr)
    const r = Number(tier?.rate ?? lr?.rate)
    if (!Number.isFinite(r) || r <= 0) return
    if (bestRate === null || r < bestRate) {
      bestRate = r
      best = { lr, tier, rate: r }
    }
  })

  return best
}

const extractMaxLvrFromCdr = (lendingRates) => {
  const arr = Array.isArray(lendingRates) ? lendingRates : []
  let max = 0
  arr.forEach((lr) => {
    const tiers = Array.isArray(lr?.tiers) ? lr.tiers : []
    tiers.forEach((t) => {
      const v = tierMaxPercent(t)
      if (Number.isFinite(v)) max = Math.max(max, v)
    })
  })
  return max || 0
}

const extractFeesFromCdr = (fees) => {
  const arr = Array.isArray(fees) ? fees : []
  const setup = arr.find((f) => {
    const t = safeLower(f?.feeType || f?.name || "")
    return t.includes("establish") || t.includes("application") || t.includes("setup")
  })

  const setupAmount = parseAmount(setup?.amount || 0)

  const ongoing = arr
    .filter((f) => {
      const t = safeLower(f?.feeType || f?.name || "")
      return t.includes("annual") || t.includes("monthly") || t.includes("ongoing") || t.includes("package")
    })
    .slice(0, 2)

  const ongoingText =
    ongoing.length > 0
      ? ongoing
          .map((f) => {
            const name = f?.name || f?.feeType || "Fee"
            const amt = f?.amount !== undefined ? Money2.format(parseAmount(f.amount)) : ""
            const period = f?.accruedPeriod || f?.period || ""
            return [name, amt, period].filter(Boolean).join(" ")
          })
          .join(" • ")
      : ""

  return { setupFee: setupAmount || 0, ongoingFeeText: ongoingText || "" }
}

const normaliseCdrProductDetail = ({ lenderKey, productDetail }) => {
  const pd = productDetail || {}
  const productId = pd?.productId || pd?.productID || pd?.id || ""
  const name = pd?.name || "Product"

  const lendingRates = pd?.lendingRates || pd?.lendingRate || []
  const fees = pd?.fees || []

  const { setupFee, ongoingFeeText } = extractFeesFromCdr(fees)
  const maxLvr = extractMaxLvrFromCdr(lendingRates) || 95

  const hasFixed = (Array.isArray(lendingRates) ? lendingRates : []).some((lr) =>
    looksFixed(lr?.rateType || lr?.lendingRateType || "")
  )
  const hasVar = (Array.isArray(lendingRates) ? lendingRates : []).some((lr) =>
    looksVariable(lr?.rateType || lr?.lendingRateType || "")
  )
  const rateTypes = []
  if (hasVar) rateTypes.push("Variable")
  if (hasFixed) rateTypes.push("Fixed")
  if (rateTypes.length === 0) rateTypes.push("Variable")

  return {
    id: `${lenderKey}:${String(productId || name)}`,
    lenderKey,
    lender: lenderKey,
    productName: name,
    rateTypes,
    rates: { ownerOccupied: 0, investment: 0 },
    rateAdjustments: { under70: 0, overOrEq70: 0 },
    setupFee: setupFee || 0,
    ongoingFeeText: ongoingFeeText || "",
    maxLvr: Number(maxLvr || 0),

    cdr: {
      productId: String(productId || ""),
      lendingRates: Array.isArray(lendingRates) ? lendingRates : [],
      fees: Array.isArray(fees) ? fees : [],
      lastFetchedAt: Date.now(),
    },
  }
}

const fetchJson = async (url, { headers = {}, timeoutMs = 15000 } = {}) => {
  const controller = new AbortController()
  const t = setTimeout(() => controller.abort(), timeoutMs)

  try {
    const res = await fetch(url, { headers, signal: controller.signal })
    if (!res.ok) {
      const text = await res.text().catch(() => "")
      throw new Error(`HTTP ${res.status} ${res.statusText} — ${text.slice(0, 180)}`)
    }
    return await res.json()
  } finally {
    clearTimeout(t)
  }
}

const fetchCdrLenderProducts = async ({ lenderKey, baseUrl, xV = 3, maxDetails = 40 }) => {
  const headers = {
    Accept: "application/json",
    "x-v": String(xV),
    "x-fapi-interaction-id": `${Date.now()}-${Math.random().toString(16).slice(2)}`,
  }

  const listUrl = `${baseUrl.replace(/\/$/, "")}/products?effective=CURRENT&page-size=1000`
  const list = await fetchJson(listUrl, { headers })

  const items = Array.isArray(list?.data?.products) ? list.data.products : []
  const homeLoanish = items.filter((p) => {
    const cat = safeLower(p?.productCategory || p?.category || "")
    return !cat || cat.includes("loan") || cat.includes("home") || cat.includes("mortgage")
  })

  const target = homeLoanish.slice(0, maxDetails)

  const detailPromises = target.map(async (p) => {
    const pid = p?.productId || p?.productID || p?.id
    if (!pid) return null
    const detailUrl = `${baseUrl.replace(/\/$/, "")}/products/${encodeURIComponent(pid)}`
    const detail = await fetchJson(detailUrl, { headers })
    const productDetail = detail?.data || detail
    return normaliseCdrProductDetail({ lenderKey, productDetail })
  })

  const settled = await Promise.allSettled(detailPromises)
  return settled.map((r) => (r.status === "fulfilled" ? r.value : null)).filter(Boolean)
}

// ----------------------------
// Displayed rate (supports legacy + CDR products)
// ----------------------------
const getDisplayedRate = ({ product, occupancy, lvr, rateTypeKey, fixedTermKey }) => {
  if (!product) return null

  if (product?.cdr?.lendingRates && Array.isArray(product.cdr.lendingRates) && product.cdr.lendingRates.length > 0) {
    const wantFixed = rateTypeKey === "FIXED"

    const picked = pickCdrLendingRate({
      lendingRates: product.cdr.lendingRates,
      occupancy,
      lvr,
      wantFixed,
    })

    const finalRate = Number(picked?.rate || 0)
    return Number.isFinite(finalRate) && finalRate > 0 ? finalRate : null
  }

  const tier = Number(lvr || 0) < 70 ? "UNDER_70" : "70_PLUS"

  const base =
    occupancy === "investment"
      ? Number(product?.rates?.investment || 0)
      : Number(product?.rates?.ownerOccupied || 0)

  const tierAdj =
    tier === "UNDER_70"
      ? Number(product?.rateAdjustments?.under70 || 0)
      : Number(product?.rateAdjustments?.overOrEq70 || 0)

  const finalRate = base + tierAdj
  return Number.isFinite(finalRate) && finalRate > 0 ? finalRate : null
}

const productMatchesRate = (product, rateTypeKey, fixedTermKey) => {
  if (product?.cdr?.lendingRates && Array.isArray(product.cdr.lendingRates) && product.cdr.lendingRates.length > 0) {
    if (rateTypeKey === "ALL") return true
    if (rateTypeKey === "VARIABLE") return (product?.rateTypes || []).some((t) => safeLower(t).includes("var"))
    if (rateTypeKey === "FIXED") return (product?.rateTypes || []).some((t) => safeLower(t).includes("fixed"))
    return true
  }

  const types = Array.isArray(product?.rateTypes) ? product.rateTypes : []

  if (rateTypeKey === "ALL") return true
  if (rateTypeKey === "VARIABLE") {
    return types.some((t) => safeLower(t).includes("var") || safeLower(t) === "variable")
  }
  if (fixedTermKey && fixedTermKey !== "ANY") {
    return types.includes(fixedTermKey)
  }
  return types.some((t) => safeLower(t).includes("fixed"))
}

const getEligibility = ({ amount, capacity, lvr, product, rateTypeKey, fixedTermKey }) => {
  const reasons = []
  const a = Number(amount || 0)
  const c = Number(capacity || 0)

  if (a > c) reasons.push(`Amount exceeds capacity by ${Money0.format(a - c)}`)

  const lv = Number(lvr || 0)
  const max = Number(product?.maxLvr || 0)
  if (max > 0 && lv > max) reasons.push(`LVR ${lv.toFixed(0)}% is above max ${max}%`)

  if (!productMatchesRate(product, rateTypeKey, fixedTermKey)) {
    if (rateTypeKey === "VARIABLE") reasons.push("Not a variable product")
    else if (rateTypeKey === "FIXED" && fixedTermKey !== "ANY") reasons.push(`Not a ${fixedTermKey} product`)
    else if (rateTypeKey === "FIXED") reasons.push("Not a fixed product")
  }

  return { works: reasons.length === 0, reasons }
}

// ----------------------------
// Logo tile (clear square block)
// ----------------------------
const Logo = ({ lenderKey, lenderName, logoUrl, size = 40 }) => {
  const [broken, setBroken] = useState(false)

  if (logoUrl && !broken) {
    return (
      <div
        style={{
          width: size,
          height: size,
          borderRadius: 8,
          border: "1px solid rgba(0,0,0,0.15)",
          background: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 6,
          boxSizing: "border-box",
        }}
        title={lenderName}
      >
        <img
          src={logoUrl}
          alt={lenderName}
          onError={() => setBroken(true)}
          style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", display: "block" }}
        />
      </div>
    )
  }

  const initials = String(lenderName || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || "")
    .join("")

  return (
    <div
      aria-label={lenderName}
      style={{
        width: size,
        height: size,
        borderRadius: 8,
        border: "1px solid rgba(0,0,0,0.15)",
        background: "#141414",
        color: "#4EDCF6",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 900,
        letterSpacing: 0.3,
        fontSize: 12,
        userSelect: "none",
        boxSizing: "border-box",
      }}
      title={lenderName}
    >
      {initials || String(lenderKey || "").slice(0, 2).toUpperCase()}
    </div>
  )
}

// ----------------------------
// SMS helper
// ----------------------------
const buildSmsMessage = ({
  lenderName,
  productName,
  occupancy,
  lvr,
  amount,
  rate,
  repaymentMonthly,
  setupFee,
  ongoingFeeText,
}) => {
  const occLabel = occupancy === "investment" ? "Investment" : "Owner Occupied"
  const lvrLabel = `${Number(lvr || 0).toFixed(0)}% LVR`
  const amt = amount ? Money0.format(amount) : ""
  const r = rate ? pct(rate) : ""
  const rep = repaymentMonthly ? `${Money0.format(repaymentMonthly)}/month (est.)` : ""
  const setup = setupFee ? Money2.format(setupFee) : ""
  const ongoing = ongoingFeeText || ""

  const bits = [
    "Mortgage Matrix — Loan option discussed:",
    lenderName ? `• Lender: ${lenderName}` : null,
    productName ? `• Product: ${productName}` : null,
    r ? `• Rate (est.): ${r}` : null,
    rep ? `• Repayments: ${rep}` : null,
    setup ? `• Setup: ${setup}` : null,
    ongoing ? `• Ongoing: ${ongoing}` : null,
    `• Type: ${occLabel}`,
    `• LVR: ${lvrLabel}`,
    amt ? `• Amount: ${amt}` : null,
    "",
    "Reply YES and we’ll lock this in, or tell us if you prefer a different lender.",
  ].filter(Boolean)

  return bits.join("\n")
}

const openSmsComposer = (mobile, bodyText) => {
  const cleaned = String(mobile || "").replace(/[^\d+]/g, "")
  const uri = `sms:${cleaned}?&body=${encodeURIComponent(bodyText || "")}`
  window.location.href = uri
}

// ----------------------------
// BID Notepad (Mercury) helper
// ----------------------------
const buildBidNotepadText = ({
  crmData,
  txnType,
  requestedAmount,
  requestedLvr,
  capacity,
  occupancy,
  rateTypeKey,
  fixedTermKey,
  lockedOptions,
  recommendedOptionId,
}) => {
  const oppId =
    crmData?.metadata?.mercuryOpportunityId || crmData?.metadata?.opportunityId || crmData?.opportunityId || "—"

  const header = [
    "=== BID / Lender Options (Generated by Broker CRM) ===",
    `Opportunity: ${oppId}`,
    `Type: ${txnType}`,
    `Occupancy: ${occupancy === "investment" ? "Investment" : "Owner Occupied"}`,
    `Amount: ${Money0.format(requestedAmount || 0)}`,
    `LVR: ${Number(requestedLvr || 0).toFixed(0)}%`,
    `Capacity (guide): ${Money0.format(capacity || 0)}`,
    `Filter: ${rateTypeKey}${rateTypeKey === "FIXED" ? ` (${fixedTermKey})` : ""}`,
    `Generated: ${new Date().toLocaleString("en-AU")}`,
    "",
    "Selected options (aim for 3 options for BID):",
    "",
  ].join("\n")

  const body =
    (lockedOptions || [])
      .slice(0, 3)
      .map((o, idx) => {
        const rec = o.optionId === recommendedOptionId ? " ✅ RECOMMENDED" : ""
        return [
          `Option ${idx + 1}${rec}`,
          `Lender: ${o.lenderName}`,
          `Product: ${o.productName}`,
          `Rate (est.): ${o.rate ? pct(o.rate) : "—"}`,
          `Repayment (est.): ${o.repaymentMonthly ? `${Money0.format(o.repaymentMonthly)}/mo` : "—"}`,
          `Setup: ${o.setupFee ? Money2.format(o.setupFee) : "—"}`,
          `Ongoing: ${o.ongoingFeeText || "—"}`,
          `Max LVR: ${o.maxLvr ? `${o.maxLvr}%` : "—"}`,
          `Notes / Rationale (broker to complete): ___________________________`,
          "",
        ].join("\n")
      })
      .join("\n") || "No options locked yet.\n"

  const footer = [
    "",
    "Compliance note:",
    "- Rates/repayments are estimates and must be verified at submission.",
    "- Final recommendation must align to client objectives and BID requirements.",
    "=== END ===",
  ].join("\n")

  return `${header}${body}${footer}`
}

const nextRecommendedOptionId = (nextLockedOptions, currentRecommendedId) => {
  if (nextLockedOptions.some((o) => o.optionId === currentRecommendedId)) return currentRecommendedId
  return nextLockedOptions[0]?.optionId || ""
}

// ----------------------------
// Modal
// ----------------------------
function ChooseLoanModal({
  open,
  onClose,
  products,
  borrowingCapacity,

  occupancy,
  onChangeOccupancy,

  amount,
  onChangeAmount,

  lvr,
  onChangeLvr,

  rateTypeKey,
  onChangeRateTypeKey,
  fixedTermKey,
  onChangeFixedTermKey,

  lenderKey,
  onChangeLenderKey,

  selectedProductId,
  onSelectProduct,

  lenderDirectory,
  onSendSms,

  onLockSelection,
}) {
  const lvrClamped = clamp(Number(lvr || 0), 1, 95)
  const tierLabel = getLvrTierLabel(lvrClamped)

  const rowsAll = useMemo(() => {
    const list = Array.isArray(products) ? products : []
    return list
      .filter((p) => productMatchesRate(p, rateTypeKey, fixedTermKey))
      .filter((p) => (!lenderKey ? true : p.lenderKey === lenderKey))
      .map((p) => {
        const lenderMeta = lenderDirectory?.[p.lenderKey] || {}
        const lenderName = lenderMeta.name || p.lender || p.lenderKey
        const rate = getDisplayedRate({ product: p, occupancy, lvr: lvrClamped, rateTypeKey, fixedTermKey })

        const { works, reasons } = getEligibility({
          amount,
          capacity: borrowingCapacity,
          lvr: lvrClamped,
          product: p,
          rateTypeKey,
          fixedTermKey,
        })

        const repaymentMonthly =
          rate && Number(amount || 0) > 0
            ? calcMonthlyRepayment({ principal: amount, annualRatePct: rate, termYears: 30 })
            : 0

        return {
          ...p,
          lenderName,
          lenderLogoUrl: lenderMeta.logoUrl || p.logoUrl,
          displayedRate: rate,
          repaymentMonthly,
          works,
          reasons,
        }
      })
  }, [products, rateTypeKey, fixedTermKey, lenderKey, lenderDirectory, occupancy, lvrClamped, amount, borrowingCapacity])

  const rowsSorted = useMemo(() => {
    const arr = [...rowsAll]
    arr.sort((a, b) => {
      if (a.works !== b.works) return a.works ? -1 : 1
      const ar = a.displayedRate ?? 999
      const br = b.displayedRate ?? 999
      return ar - br
    })
    return arr
  }, [rowsAll])

  const eligibleCheapestIds = useMemo(() => {
    const eligible = rowsSorted.filter((r) => r.works && Number.isFinite(r.displayedRate))
    return new Set(eligible.slice(0, 3).map((r) => r.id))
  }, [rowsSorted])

  const lenders = useMemo(() => {
    const map = new Map()
    rowsAll.forEach((r) => {
      if (!map.has(r.lenderKey)) map.set(r.lenderKey, { lenderKey: r.lenderKey, name: r.lenderName })
    })
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name))
  }, [rowsAll])

  const selectedRow = useMemo(() => rowsAll.find((r) => r.id === selectedProductId) || null, [rowsAll, selectedProductId])

  if (!open) return null

  const ui = {
    label: { fontSize: 12, fontWeight: 800, color: "#141414", marginBottom: 6 },
    muted: { fontSize: 12, opacity: 0.72 },
    input: {
      width: "100%",
      border: "1px solid rgba(0,0,0,0.12)",
      borderRadius: 12,
      padding: "10px 12px",
      fontWeight: 800,
      fontSize: 13,
      outline: "none",
      background: "#fff",
    },
    chipBtn: (active) => ({
      border: "1px solid rgba(0,0,0,0.12)",
      background: active ? "#141414" : "#fff",
      color: active ? "#4EDCF6" : "#141414",
      borderRadius: 12,
      padding: "10px 12px",
      fontWeight: 900,
      fontSize: 13,
      cursor: "pointer",
      minWidth: 150,
    }),
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 12,
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.()
      }}
    >
      <div
        style={{
          width: "min(1200px, 98vw)",
          maxHeight: "92vh",
          background: "#ffffff",
          borderRadius: 16,
          overflow: "hidden",
          boxShadow: "0 18px 45px rgba(0,0,0,0.28)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            background: "#4EDCF6",
            color: "#141414",
            padding: "12px 14px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
          }}
        >
          <div style={{ fontWeight: 900, letterSpacing: 0.2, fontSize: 14 }}>Choose your loan</div>
          <button
            type="button"
            onClick={onClose}
            style={{
              border: "none",
              background: "rgba(20,20,20,0.12)",
              color: "#141414",
              borderRadius: 12,
              padding: "8px 10px",
              cursor: "pointer",
              fontWeight: 900,
            }}
          >
            ✕
          </button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "360px 1fr", minHeight: 0 }}>
          <div style={{ borderRight: "1px solid rgba(0,0,0,0.06)", padding: 14, overflow: "auto", minHeight: 0 }}>
            <div style={{ display: "grid", gap: 12 }}>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button
                  type="button"
                  onClick={() => onChangeOccupancy("ownerOccupied")}
                  style={ui.chipBtn(occupancy === "ownerOccupied")}
                >
                  Owner Occupied
                </button>
                <button
                  type="button"
                  onClick={() => onChangeOccupancy("investment")}
                  style={ui.chipBtn(occupancy === "investment")}
                >
                  Investment
                </button>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div>
                  <div style={ui.label}>Amount</div>
                  <input
                    value={String(amount ?? "")}
                    onChange={(e) => onChangeAmount(parseAmount(e.target.value))}
                    inputMode="numeric"
                    style={ui.input}
                  />
                  <div style={{ ...ui.muted, marginTop: 8 }}>
                    Capacity: <strong>{Money0.format(borrowingCapacity || 0)}</strong>
                  </div>
                </div>

                <div>
                  <div style={ui.label}>LVR (max 95%)</div>
                  <input
                    value={String(lvrClamped.toFixed(0))}
                    onChange={(e) => onChangeLvr(clamp(parseAmount(e.target.value), 1, 95))}
                    inputMode="numeric"
                    style={ui.input}
                  />
                  <div style={{ ...ui.muted, marginTop: 8 }}>{tierLabel || "—"}</div>
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div>
                  <div style={ui.label}>Rate type</div>
                  <select
                    value={rateTypeKey}
                    onChange={(e) => onChangeRateTypeKey(e.target.value)}
                    style={{ ...ui.input, height: 44 }}
                  >
                    {RATE_TYPE.map((rt) => (
                      <option key={rt.key} value={rt.key}>
                        {rt.label}
                      </option>
                    ))}
                  </select>
                  <div style={{ ...ui.muted, marginTop: 8 }}>No default — choose Variable or Fixed, or leave as All.</div>
                </div>

                <div>
                  <div style={ui.label}>Fixed term</div>
                  <select
                    value={fixedTermKey}
                    onChange={(e) => onChangeFixedTermKey(e.target.value)}
                    style={{
                      ...ui.input,
                      height: 44,
                      opacity: rateTypeKey === "FIXED" ? 1 : 0.55,
                      cursor: rateTypeKey === "FIXED" ? "pointer" : "not-allowed",
                    }}
                    disabled={rateTypeKey !== "FIXED"}
                  >
                    {FIXED_TERMS.map((t) => (
                      <option key={t.key} value={t.key}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <div style={ui.label}>Lender</div>
                <select value={lenderKey} onChange={(e) => onChangeLenderKey(e.target.value)} style={{ ...ui.input, height: 44 }}>
                  <option value="">All lenders</option>
                  {lenders.map((l) => (
                    <option key={l.lenderKey} value={l.lenderKey}>
                      {l.name}
                    </option>
                  ))}
                </select>
                <div style={{ ...ui.muted, marginTop: 8 }}>
                  Showing <strong>{rowsSorted.length}</strong> option(s)
                </div>
              </div>

              <div
                style={{
                  border: "2px solid rgba(0,0,0,0.10)",
                  borderRadius: 12,
                  padding: 12,
                  background: "#fff",
                }}
              >
                <div style={{ fontWeight: 900, marginBottom: 8, fontSize: 13, color: "#141414" }}>Selection</div>

                {selectedRow ? (
                  <>
                    <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                      <Logo lenderKey={selectedRow.lenderKey} lenderName={selectedRow.lenderName} logoUrl={selectedRow.lenderLogoUrl} size={42} />
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 900, color: "#141414", fontSize: 13 }}>{selectedRow.lenderName}</div>
                        <div style={{ fontSize: 12, opacity: 0.78 }}>{selectedRow.productName}</div>
                      </div>
                    </div>

                    <div style={{ marginTop: 10, fontSize: 12, opacity: 0.8 }}>
                      Rate (est.): <strong>{selectedRow.displayedRate ? pct(selectedRow.displayedRate) : "—"}</strong>
                      {" • "}Repay (est.):{" "}
                      <strong>{selectedRow.repaymentMonthly ? `${Money0.format(selectedRow.repaymentMonthly)}/mo` : "—"}</strong>
                      {" • "}Max LVR: <strong>{selectedRow.maxLvr}%</strong>
                    </div>

                    {!selectedRow.works && selectedRow.reasons?.length > 0 && (
                      <div style={{ marginTop: 10, fontSize: 12, color: "#8a0000" }}>
                        <strong>Not eligible:</strong> {selectedRow.reasons.join(" • ")}
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ fontSize: 12, opacity: 0.75 }}>Select a product on the right.</div>
                )}
              </div>

              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button
                  type="button"
                  onClick={() => {
                    if (!selectedRow) return
                    onLockSelection?.(selectedRow)
                    onClose?.()
                  }}
                  disabled={!selectedRow}
                  style={{
                    border: "none",
                    background: !selectedRow ? "#ddd" : "#141414",
                    color: !selectedRow ? "#666" : "#fff",
                    borderRadius: 12,
                    padding: "11px 12px",
                    fontWeight: 900,
                    fontSize: 13,
                    cursor: !selectedRow ? "not-allowed" : "pointer",
                    flex: "1 1 160px",
                  }}
                >
                  Add to BID options (max 3)
                </button>

                <button
                  type="button"
                  onClick={() => selectedRow && onSendSms?.(selectedRow)}
                  disabled={!selectedRow}
                  style={{
                    border: "2px solid rgba(0,0,0,0.12)",
                    background: "#fff",
                    color: "#141414",
                    borderRadius: 12,
                    padding: "11px 12px",
                    fontWeight: 900,
                    fontSize: 13,
                    cursor: !selectedRow ? "not-allowed" : "pointer",
                    flex: "1 1 200px",
                  }}
                >
                  Send to client (SMS)
                </button>
              </div>

              <div style={ui.muted}>“Not eligible” is usually amount above capacity, LVR above product max, or rate-type mismatch.</div>
            </div>
          </div>

          <div style={{ padding: 14, overflow: "auto", minHeight: 0 }}>
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
              <div style={{ fontWeight: 900, color: "#141414", fontSize: 13 }}>Products</div>
              <div style={{ fontSize: 12, opacity: 0.72 }}>
                Showing <strong>{rowsSorted.length}</strong> option(s)
              </div>
            </div>

            <div
              style={{
                marginTop: 10,
                border: "2px solid rgba(0,0,0,0.10)",
                borderRadius: 12,
                overflow: "hidden",
                background: "#fff",
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "36px 1.2fr 0.6fr 0.5fr 0.6fr 0.45fr",
                  gap: 10,
                  padding: "10px 12px",
                  background: "rgba(0,0,0,0.03)",
                  fontWeight: 900,
                  fontSize: 12,
                  color: "#141414",
                  borderBottom: "2px solid rgba(0,0,0,0.08)",
                }}
              >
                <div></div>
                <div>Lender / Product</div>
                <div>Rate (est.)</div>
                <div>Setup</div>
                <div>Ongoing</div>
                <div>Max LVR</div>
              </div>

              <div style={{ display: "grid", gap: 10, padding: 12, background: "#fff" }}>
                {rowsSorted.map((r) => {
                  const selected = r.id === selectedProductId
                  const highlight = eligibleCheapestIds.has(r.id)

                  return (
                    <button
                      key={r.id}
                      type="button"
                      onClick={() => onSelectProduct(r.id)}
                      style={{
                        width: "100%",
                        border: selected
                          ? "2px solid rgba(78,220,246,0.95)"
                          : highlight
                          ? "2px solid rgba(78,220,246,0.55)"
                          : "2px solid rgba(0,0,0,0.10)",
                        background: selected
                          ? "rgba(78,220,246,0.14)"
                          : highlight
                          ? "rgba(78,220,246,0.08)"
                          : "#fff",
                        borderRadius: 12,
                        textAlign: "left",
                        cursor: "pointer",
                        padding: 16,
                        opacity: r.works ? 1 : 0.62,
                      }}
                      title={r.works ? "Select this product" : `Not eligible: ${r.reasons?.join(" • ") || ""}`}
                    >
                      <div style={{ display: "grid", gridTemplateColumns: "36px 1.2fr 0.6fr 0.5fr 0.6fr 0.45fr", gap: 10, alignItems: "center" }}>
                        <div>
                          <input type="radio" readOnly checked={selected} />
                        </div>

                        <div style={{ display: "flex", gap: 10, alignItems: "center", minWidth: 0 }}>
                          <Logo lenderKey={r.lenderKey} lenderName={r.lenderName} logoUrl={r.lenderLogoUrl} size={42} />
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 900, fontSize: 13, color: "#141414" }}>{r.lenderName}</div>
                            <div style={{ fontSize: 12, opacity: 0.72 }}>
                              {r.productName}{" "}
                              {highlight ? (
                                <span
                                  style={{
                                    marginLeft: 8,
                                    fontSize: 11,
                                    fontWeight: 900,
                                    padding: "2px 8px",
                                    borderRadius: 999,
                                    border: "1px solid rgba(78,220,246,0.65)",
                                    background: "rgba(78,220,246,0.18)",
                                    color: "#141414",
                                  }}
                                >
                                  Top rate
                                </span>
                              ) : null}
                            </div>

                            {!r.works && r.reasons?.length > 0 && (
                              <div style={{ fontSize: 12, color: "#8a0000", marginTop: 4 }}>
                                {r.reasons[0]}
                                {r.reasons.length > 1 ? "…" : ""}
                              </div>
                            )}
                          </div>
                        </div>

                        <div style={{ fontWeight: 950, fontSize: 13, color: "#141414" }}>
                          {r.displayedRate ? pct(r.displayedRate) : "—"}
                          <div style={{ fontSize: 11, opacity: 0.7, fontWeight: 900 }}>{tierLabel || "—"}</div>
                          <div style={{ fontSize: 11, opacity: 0.7, fontWeight: 900 }}>
                            {r.repaymentMonthly ? `${Money0.format(r.repaymentMonthly)}/mo` : "—"}
                          </div>
                        </div>

                        <div style={{ fontWeight: 900, fontSize: 13 }}>{r.setupFee ? Money2.format(r.setupFee) : "—"}</div>
                        <div style={{ fontWeight: 900, fontSize: 13 }}>{r.ongoingFeeText || "—"}</div>
                        <div style={{ fontWeight: 950, fontSize: 13 }}>{r.maxLvr}%</div>
                      </div>
                    </button>
                  )
                })}

                {rowsSorted.length === 0 && <div style={{ padding: 14, fontSize: 12, opacity: 0.75 }}>No products match your selection.</div>}
              </div>
            </div>

            <div style={{ marginTop: 10, fontSize: 12, opacity: 0.78 }}>
              <strong>Tip:</strong> highlighted rows are the <strong>top 3 cheapest eligible</strong> rates.
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// -----------------------------------------------------
// Component
// -----------------------------------------------------
function BorrowingCapacityStep({
  crmData = {},
  onBack,
  onNext,
  onSendSelectedBankSms,
  clientMobile,
  onWriteMercuryNotepad,
}) {
  const [capacity, setCapacity] = useState(0)

  const [pickerOpen, setPickerOpen] = useState(false)
  const [requestedAmount, setRequestedAmount] = useState(0)
  const [requestedLvr, setRequestedLvr] = useState(80)

  const [occupancy, setOccupancy] = useState("ownerOccupied")

  const [rateTypeKey, setRateTypeKey] = useState("ALL")
  const [fixedTermKey, setFixedTermKey] = useState("ANY")

  const [lenderKey, setLenderKey] = useState("")
  const [selectedProductId, setSelectedProductId] = useState("")

  const [lockedOptions, setLockedOptions] = useState([])
  const [recommendedOptionId, setRecommendedOptionId] = useState("")

  const persistKey = useMemo(() => getPersistKey(crmData), [crmData])

  const [liveProducts, setLiveProducts] = useState([])
  const [liveStatus, setLiveStatus] = useState({ loading: false, error: "" })

  useEffect(() => {
    try {
      const raw = localStorage.getItem(persistKey)
      if (!raw) return
      const data = JSON.parse(raw)

      if (data?.selectedProductId) setSelectedProductId(String(data.selectedProductId))
      if (data?.occupancy) setOccupancy(data.occupancy === "investment" ? "investment" : "ownerOccupied")

      if (Number.isFinite(Number(data?.requestedAmount))) setRequestedAmount(Number(data.requestedAmount))
      if (Number.isFinite(Number(data?.requestedLvr))) setRequestedLvr(clamp(Number(data.requestedLvr), 1, 95))

      if (data?.rateTypeKey) setRateTypeKey(String(data.rateTypeKey))
      if (data?.fixedTermKey) setFixedTermKey(String(data.fixedTermKey))
      if (data?.lenderKey !== undefined) setLenderKey(String(data.lenderKey || ""))

      if (Array.isArray(data?.lockedOptions)) setLockedOptions(data.lockedOptions.slice(0, 3))
      if (data?.recommendedOptionId) setRecommendedOptionId(String(data.recommendedOptionId || ""))
    } catch {
      // ignore
    }
  }, [persistKey])

  useEffect(() => {
    const t = setTimeout(() => {
      try {
        localStorage.setItem(
          persistKey,
          JSON.stringify({
            selectedProductId,
            occupancy,
            requestedAmount,
            requestedLvr,
            rateTypeKey,
            fixedTermKey,
            lenderKey,
            lockedOptions,
            recommendedOptionId,
          })
        )
      } catch {
        // ignore
      }
    }, 180)

    return () => clearTimeout(t)
  }, [
    persistKey,
    selectedProductId,
    occupancy,
    requestedAmount,
    requestedLvr,
    rateTypeKey,
    fixedTermKey,
    lenderKey,
    lockedOptions,
    recommendedOptionId,
  ])

  useEffect(() => {
    if (rateTypeKey !== "FIXED" && fixedTermKey !== "ANY") setFixedTermKey("ANY")
  }, [rateTypeKey, fixedTermKey])

  useEffect(() => {
    setRequestedLvr((v) => clamp(Number(v || 0), 1, 95))
  }, [])

  const lenderDirectory = useMemo(
    () => ({
      amp: { name: "AMP", logoUrl: "/bank-logos/AMP.png" },
      anz: { name: "ANZ", logoUrl: "/bank-logos/ANZ.png" },
      auswide: { name: "Auswide Bank", logoUrl: "/bank-logos/Auswide.png" },
      bankofchina: { name: "Bank of China", logoUrl: "/bank-logos/Bank_Of_China_logo.png" },
      bankofmelbourne: { name: "Bank of Melbourne", logoUrl: "" },
      bankwest: { name: "Bankwest", logoUrl: "/bank-logos/Bankwest.png" },
      boq: { name: "BOQ", logoUrl: "" },
      cba: { name: "Commonwealth Bank", logoUrl: "/bank-logos/commonwealth-bank.png" },
      firstmac: { name: "Firstmac", logoUrl: "/bank-logos/firstmac.jpg" },
      ing: { name: "ING", logoUrl: "/bank-logos/ING.png" },
      latrobe: { name: "La Trobe", logoUrl: "/bank-logos/la trobe.jpeg" },
      macquarie: { name: "Macquarie", logoUrl: "/bank-logos/Macquarie.png" },
      mebank: { name: "ME Bank", logoUrl: "/bank-logos/ME Bank.jpg" },
      nab: { name: "NAB", logoUrl: "/bank-logos/NAB.jpg" },
      pepper: { name: "Pepper Money", logoUrl: "/bank-logos/Pepper.JPG" },
      stgeorge: { name: "St.George", logoUrl: "/bank-logos/STG.JPG" },
      suncorp: { name: "Suncorp", logoUrl: "/bank-logos/SuncorpBank.jpg" },
      westpac: { name: "Westpac", logoUrl: "/bank-logos/westpac.png" },
      ubank: { name: "ubank", logoUrl: "" },
    }),
    []
  )

  const curatedProducts = useMemo(
    () => [
      {
        id: "ubank-var",
        lenderKey: "ubank",
        lender: "ubank",
        productName: "Variable",
        rateTypes: ["Variable"],
        rates: { ownerOccupied: 5.59, investment: 5.89 },
        rateAdjustments: { under70: -0.18, overOrEq70: 0 },
        setupFee: 250,
        ongoingFeeText: "$250.00 (annual)",
        maxLvr: 90,
      },
      {
        id: "suncorp-var",
        lenderKey: "suncorp",
        lender: "Suncorp",
        productName: "Back to Basics",
        rateTypes: ["Variable"],
        rates: { ownerOccupied: 5.65, investment: 5.90 },
        rateAdjustments: { under70: -0.10, overOrEq70: 0 },
        setupFee: 0,
        ongoingFeeText: "",
        maxLvr: 90,
      },
      {
        id: "westpac-var",
        lenderKey: "westpac",
        lender: "Westpac",
        productName: "Flexi First Option",
        rateTypes: ["Variable"],
        rates: { ownerOccupied: 5.69, investment: 5.99 },
        rateAdjustments: { under70: -0.15, overOrEq70: 0 },
        setupFee: 350,
        ongoingFeeText: "$395.00 (annual)",
        maxLvr: 95,
      },
      {
        id: "anz-var",
        lenderKey: "anz",
        lender: "ANZ",
        productName: "Standard Variable",
        rateTypes: ["Variable"],
        rates: { ownerOccupied: 5.69, investment: 5.99 },
        rateAdjustments: { under70: -0.12, overOrEq70: 0 },
        setupFee: 0,
        ongoingFeeText: "$10.00 (monthly)",
        maxLvr: 97,
      },
      {
        id: "ing-var",
        lenderKey: "ing",
        lender: "ING",
        productName: "Orange Advantage",
        rateTypes: ["Variable"],
        rates: { ownerOccupied: 5.74, investment: 6.04 },
        rateAdjustments: { under70: -0.20, overOrEq70: 0 },
        setupFee: 350,
        ongoingFeeText: "$299.00 (annual)",
        maxLvr: 95,
      },
      {
        id: "bankwest-fixed2",
        lenderKey: "bankwest",
        lender: "Bankwest",
        productName: "2 Year Fixed",
        rateTypes: ["Fixed 2yrs"],
        rates: { ownerOccupied: 5.74, investment: 6.04 },
        rateAdjustments: { under70: -0.10, overOrEq70: 0 },
        setupFee: 350,
        ongoingFeeText: "$395.00 (annual)",
        maxLvr: 98,
      },
      {
        id: "stgeorge-fixed1",
        lenderKey: "stgeorge",
        lender: "St.George",
        productName: "1 Year Fixed",
        rateTypes: ["Fixed 1yr"],
        rates: { ownerOccupied: 5.79, investment: 6.09 },
        rateAdjustments: { under70: -0.12, overOrEq70: 0 },
        setupFee: 100,
        ongoingFeeText: "$395.00 (annual)",
        maxLvr: 95,
      },
      {
        id: "macquarie-var",
        lenderKey: "macquarie",
        lender: "Macquarie",
        productName: "Variable",
        rateTypes: ["Variable"],
        rates: { ownerOccupied: 5.79, investment: 6.09 },
        rateAdjustments: { under70: -0.18, overOrEq70: 0 },
        setupFee: 350,
        ongoingFeeText: "$248.00 (annual)",
        maxLvr: 90,
      },
    ],
    []
  )

  useEffect(() => {
    let cancelled = false

    const run = async () => {
      setLiveStatus({ loading: true, error: "" })

      try {
        const [anz, westpac, cba] = await Promise.allSettled([
          fetchCdrLenderProducts({
            lenderKey: "anz",
            baseUrl: "https://api.anz/cds-au/v1/banking",
            xV: 3,
            maxDetails: 40,
          }),
          fetchCdrLenderProducts({
            lenderKey: "westpac",
            baseUrl: "https://digital-api.westpac.com.au/cds-au/v1/banking",
            xV: 3,
            maxDetails: 40,
          }),
          fetchCdrLenderProducts({
            lenderKey: "cba",
            baseUrl: "https://api.commbank.com.au/public/cds-au/v1/banking",
            xV: 3,
            maxDetails: 40,
          }),
        ])

        const merged = []
        ;[anz, westpac, cba].forEach((r) => {
          if (r.status === "fulfilled") merged.push(...(r.value || []))
        })

        if (!cancelled) {
          setLiveProducts(merged)
          setLiveStatus({ loading: false, error: "" })
        }
      } catch (e) {
        if (!cancelled) setLiveStatus({ loading: false, error: e?.message || "Failed to load live products" })
      }
    }

    run()
    return () => {
      cancelled = true
    }
  }, [])

  const products = useMemo(() => {
    const curated = Array.isArray(curatedProducts) ? curatedProducts : []
    const live = Array.isArray(liveProducts) ? liveProducts : []

    const seen = new Set()
    const out = []

    ;[...curated, ...live].forEach((p) => {
      const id = String(p?.id || "")
      if (!id) return
      if (seen.has(id)) return
      seen.add(id)
      out.push(p)
    })

    return out
  }, [curatedProducts, liveProducts])

  useEffect(() => {
    const applicants = Array.isArray(crmData.applicants) ? crmData.applicants : []
    const numberOfApplicants = Math.min(applicants.length, 2)

    if (numberOfApplicants === 0) {
      setCapacity(0)
      return
    }

    const dependants = Number(applicants[0]?.dependants || 0)
    const items = Array.isArray(crmData.income) ? crmData.income : []

    const annualIncome1 = getAnnualIncomeForOwner(items, "Applicant 1")
    const annualIncome2 = numberOfApplicants === 2 ? getAnnualIncomeForOwner(items, "Applicant 2") : 0

    const totalAnnualIncome = annualIncome1 + annualIncome2
    const rentalMonthly = getMonthlyRentalIncome(items)

    const { creditCardLimit, personalLoanRepay, homeLoanRepay } = getLiabilitySummaries(crmData.assetsLiabilities || {})

    const creditCardAnnualAssessment = creditCardLimit * 0.038
    const creditCardMonthlyAssessment = creditCardAnnualAssessment / 12

    const hemApplicants = HemSingleJoint(totalAnnualIncome, numberOfApplicants)
    const hemDependantAmt = HemDependant(numberOfApplicants, dependants)

    const net1 = CalculateNetIncome(annualIncome1)
    const net2 = numberOfApplicants === 2 ? CalculateNetIncome(annualIncome2) : 0
    const netMonthly = numberOfApplicants === 1 ? net1 / 12 : (net1 + net2) / 12

    const expenses = creditCardMonthlyAssessment + homeLoanRepay + personalLoanRepay + hemApplicants + hemDependantAmt
    const surplus = Math.max(0, netMonthly + rentalMonthly - expenses)

    const baseRate = 5.45
    const buffer = 3
    const assessmentRate = baseRate + buffer
    const years = 30

    let maxLoan = 0
    if (surplus > 0) {
      maxLoan = CalculatePV(assessmentRate, years * 12, surplus)
    }

    setCapacity(maxLoan)
  }, [crmData])

  useEffect(() => {
    const txn = getTxnType(crmData)

    let nextAmount = 0
    let nextValue = 0

    if (txn === "purchase") {
      const price = getPurchasePrice(crmData)
      const deposit = getDepositAmount(crmData)
      nextAmount = Math.max(0, price - deposit)
      nextValue = price || 0
    } else {
      nextAmount =
        parseAmount(crmData?.refinance?.loanAmount) ||
        parseAmount(crmData?.refinanceDetails?.loanAmount) ||
        sumHomeLoanBalances(crmData?.assetsLiabilities || {}) ||
        0
      nextValue = getPropertyValue(crmData) || 0
    }

    if (nextAmount > 0) setRequestedAmount((prev) => (prev > 0 ? prev : Math.round(nextAmount)))

    if (nextAmount > 0 && nextValue > 0) {
      const lvrCalc = (nextAmount / nextValue) * 100
      setRequestedLvr(clamp(lvrCalc, 1, 95))
    } else {
      setRequestedLvr((prev) => clamp(prev, 1, 95))
    }
  }, [crmData])

  const txnType = getTxnType(crmData) || "—"

  const writeNotepad = async (nextLockedOptions, nextRecommendedId) => {
    const noteText = buildBidNotepadText({
      crmData,
      txnType,
      requestedAmount,
      requestedLvr,
      capacity,
      occupancy,
      rateTypeKey,
      fixedTermKey,
      lockedOptions: nextLockedOptions,
      recommendedOptionId: nextRecommendedId,
    })

    if (typeof onWriteMercuryNotepad === "function") {
      try {
        await onWriteMercuryNotepad(noteText)
      } catch {
        // fail silently
      }
    }
  }

  const handleSendSms = async (productRow) => {
    const lenderName = lenderDirectory[productRow.lenderKey]?.name || productRow.lender
    const rate = getDisplayedRate({
      product: productRow,
      occupancy,
      lvr: clamp(requestedLvr, 1, 95),
      rateTypeKey,
      fixedTermKey,
    })
    const repaymentMonthly =
      rate && Number(requestedAmount || 0) > 0
        ? calcMonthlyRepayment({ principal: requestedAmount, annualRatePct: rate, termYears: 30 })
        : 0

    const message = buildSmsMessage({
      lenderName,
      productName: productRow.productName,
      occupancy,
      lvr: requestedLvr,
      amount: requestedAmount,
      rate,
      repaymentMonthly,
      setupFee: productRow.setupFee || 0,
      ongoingFeeText: productRow.ongoingFeeText || "",
    })

    if (typeof onSendSelectedBankSms === "function") {
      await onSendSelectedBankSms({
        mobile: clientMobile,
        message,
        lenderKey: productRow.lenderKey,
        lenderName,
        productId: productRow.id,
        productName: productRow.productName,
      })
      return
    }

    openSmsComposer(clientMobile || "", message)
  }

  const handleLockSelection = async (row) => {
    if (!row) return

    const rate = getDisplayedRate({
      product: row,
      occupancy,
      lvr: clamp(requestedLvr, 1, 95),
      rateTypeKey,
      fixedTermKey,
    })

    const repaymentMonthly =
      rate && Number(requestedAmount || 0) > 0
        ? calcMonthlyRepayment({ principal: requestedAmount, annualRatePct: rate, termYears: 30 })
        : 0

    const optionId = `${row.id}::${occupancy}::${requestedAmount}::${requestedLvr}`

    const next = [
      ...(lockedOptions || []).filter((o) => o.optionId !== optionId),
      {
        optionId,
        productId: row.id,
        lenderKey: row.lenderKey,
        lenderName: row.lenderName,
        productName: row.productName,
        rate: rate || null,
        repaymentMonthly: repaymentMonthly || 0,
        setupFee: row.setupFee || 0,
        ongoingFeeText: row.ongoingFeeText || "",
        maxLvr: row.maxLvr || 0,
        occupancy,
        requestedAmount,
        requestedLvr,
      },
    ].slice(-3)

    const nextRecommended = recommendedOptionId || next[0]?.optionId || ""

    setLockedOptions(next)
    setRecommendedOptionId(nextRecommended)
    setSelectedProductId(row.id)

    await writeNotepad(next, nextRecommended)
  }

  const lvrTier = getLvrTierLabel(requestedLvr)

  const page = {
    title: { fontSize: 28, fontWeight: 900, margin: 0, color: "#141414" },
    sub: { marginTop: 6, fontSize: 14, opacity: 0.75, fontWeight: 700 },
    card: {
      border: "1px solid rgba(0,0,0,0.08)",
      borderRadius: 18,
      background: "#fff",
      boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
    },
    pill: {
      border: "1px solid rgba(0,0,0,0.10)",
      borderRadius: 999,
      padding: "8px 12px",
      fontSize: 13,
      fontWeight: 800,
      background: "#fff",
      color: "#141414",
      display: "inline-flex",
      gap: 8,
      alignItems: "center",
    },
    select: {
      width: "100%",
      height: 52,
      borderRadius: 14,
      border: "1px solid rgba(0,0,0,0.12)",
      padding: "0 14px",
      fontWeight: 900,
      fontSize: 18,
      background: "#fff",
      color: "#141414",
      outline: "none",
    },
    liveNote: { marginTop: 10, fontSize: 12, opacity: 0.75, fontWeight: 800 },
    liveError: { marginTop: 10, fontSize: 12, color: "#8a0000", fontWeight: 900 },
  }

  return (
    <div>
      <h2 style={page.title}>Borrowing Capacity</h2>
      <div style={page.sub}>
        Guide only. Lock up to <strong>3</strong> lender options for BID, and mark <strong>1</strong> as recommended.
      </div>

      <div style={{ ...page.card, padding: 18, marginTop: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 900, opacity: 0.7 }}>Estimated maximum</div>
        <div style={{ fontSize: 54, fontWeight: 950, marginTop: 6, color: "#141414" }}>{Money0.format(capacity)}</div>

        <div style={{ marginTop: 6, fontSize: 13, opacity: 0.75, fontWeight: 700 }}>
          Servicing guide only. Use as an internal indication before BID and lender selection.
        </div>

        {liveStatus.loading && <div style={page.liveNote}>Loading live products (ANZ / Westpac / CBA)…</div>}
        {!liveStatus.loading && liveProducts.length > 0 && (
          <div style={page.liveNote}>
            Live products loaded: <strong>{liveProducts.length}</strong> (ANZ / Westpac / CBA).
          </div>
        )}
        {!liveStatus.loading && !!liveStatus.error && <div style={page.liveError}>Live products failed: {liveStatus.error}</div>}

        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          style={{
            marginTop: 14,
            width: "100%",
            height: 56,
            borderRadius: 14,
            border: "none",
            background: "#141414",
            color: "#fff",
            fontWeight: 950,
            fontSize: 20,
            cursor: "pointer",
          }}
        >
          Choose your loan
        </button>

        <div style={{ marginTop: 12, display: "grid", gap: 10 }}>
          <select
            value={occupancy === "investment" ? "Investment" : "Owner Occupied"}
            onChange={(e) => setOccupancy(e.target.value === "Investment" ? "investment" : "ownerOccupied")}
            style={page.select}
          >
            <option>Owner Occupied</option>
            <option>Investment</option>
          </select>

          <select value={rateTypeKey} onChange={(e) => setRateTypeKey(e.target.value)} style={page.select}>
            <option value="ALL">Rate type: All</option>
            <option value="VARIABLE">Rate type: Variable</option>
            <option value="FIXED">Rate type: Fixed</option>
          </select>

          <select
            value={fixedTermKey}
            onChange={(e) => setFixedTermKey(e.target.value)}
            style={{
              ...page.select,
              opacity: rateTypeKey === "FIXED" ? 1 : 0.55,
              cursor: rateTypeKey === "FIXED" ? "pointer" : "not-allowed",
            }}
            disabled={rateTypeKey !== "FIXED"}
          >
            <option value="ANY">Fixed term: Any</option>
            <option value="Fixed 1yr">Fixed term: 1 year</option>
            <option value="Fixed 2yrs">Fixed term: 2 years</option>
            <option value="Fixed 3yrs">Fixed term: 3 years</option>
            <option value="Fixed 4yrs">Fixed term: 4 years</option>
            <option value="Fixed 5yrs">Fixed term: 5 years</option>
          </select>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginTop: 14 }}>
          <div style={{ border: "1px solid rgba(78,220,246,0.55)", background: "rgba(78,220,246,0.12)", borderRadius: 16, padding: 14, minHeight: 88 }}>
            <div style={{ fontSize: 13, fontWeight: 900, opacity: 0.7 }}>Loan amount (auto)</div>
            <div style={{ fontSize: 22, fontWeight: 950, marginTop: 6 }}>{Money0.format(requestedAmount)}</div>
            <div style={{ fontSize: 13, opacity: 0.8, fontWeight: 800, marginTop: 4 }}>
              {txnType === "purchase" ? "Purchase: price − deposit" : "Refi: balances / loan amount"}
            </div>
          </div>

          <div style={{ border: "1px solid rgba(78,220,246,0.55)", background: "rgba(78,220,246,0.12)", borderRadius: 16, padding: 14, minHeight: 88 }}>
            <div style={{ fontSize: 13, fontWeight: 900, opacity: 0.7 }}>LVR (max 95%)</div>
            <div style={{ fontSize: 22, fontWeight: 950, marginTop: 6 }}>{clamp(requestedLvr, 1, 95).toFixed(0)}%</div>
            <div style={{ fontSize: 13, opacity: 0.8, fontWeight: 800, marginTop: 4 }}>{lvrTier || "—"}</div>
          </div>

          <div style={{ border: "1px solid rgba(78,220,246,0.55)", background: "rgba(78,220,246,0.12)", borderRadius: 16, padding: 14, minHeight: 88 }}>
            <div style={{ fontSize: 13, fontWeight: 900, opacity: 0.7 }}>BID Options</div>
            <div style={{ fontSize: 22, fontWeight: 950, marginTop: 6 }}>{Math.min(lockedOptions.length, 3)}/3 locked</div>
            <div style={{ fontSize: 13, opacity: 0.8, fontWeight: 800, marginTop: 4 }}>
              Recommended:{" "}
              <strong>
                {lockedOptions.find((o) => o.optionId === recommendedOptionId)?.lenderName || "—"}
              </strong>
            </div>
          </div>
        </div>

        <div style={{ marginTop: 14 }}>
          <div style={{ fontWeight: 950, marginBottom: 10, fontSize: 13 }}>Locked lender options (BID)</div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
            {[0, 1, 2].map((i) => {
              const o = lockedOptions[i]
              const isRec = o?.optionId && o.optionId === recommendedOptionId

              return (
                <div
                  key={i}
                  style={{
                    border: isRec ? "2px solid rgba(78,220,246,0.95)" : "2px solid rgba(0,0,0,0.12)",
                    borderRadius: 14,
                    padding: 14,
                    background: "#fff",
                    minHeight: 170,
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                    <div style={{ fontWeight: 950 }}>Option {i + 1}</div>
                    {isRec ? (
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 950,
                          padding: "4px 8px",
                          borderRadius: 999,
                          border: "1px solid rgba(78,220,246,0.8)",
                          background: "rgba(78,220,246,0.18)",
                        }}
                      >
                        Recommended
                      </span>
                    ) : null}
                  </div>

                  {!o ? (
                    <div style={{ marginTop: 10, fontSize: 12, opacity: 0.75 }}>No lender locked yet.</div>
                  ) : (
                    <>
                      <div style={{ marginTop: 10, display: "flex", gap: 10, alignItems: "center" }}>
                        <Logo
                          lenderKey={o.lenderKey}
                          lenderName={o.lenderName}
                          logoUrl={lenderDirectory?.[o.lenderKey]?.logoUrl}
                          size={42}
                        />
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 950, fontSize: 13 }}>{o.lenderName}</div>
                          <div style={{ fontSize: 12, opacity: 0.75 }}>{o.productName}</div>
                        </div>
                      </div>

                      <div style={{ marginTop: 10, fontSize: 12, fontWeight: 850, opacity: 0.85, lineHeight: 1.45 }}>
                        <div>
                          Rate: <strong>{o.rate ? pct(o.rate) : "—"}</strong>
                        </div>
                        <div>
                          Repay: <strong>{o.repaymentMonthly ? `${Money0.format(o.repaymentMonthly)}/mo` : "—"}</strong>
                        </div>
                        <div>
                          Fees: <strong>{o.setupFee ? Money2.format(o.setupFee) : "—"}</strong> •{" "}
                          <strong>{o.ongoingFeeText || "—"}</strong>
                        </div>
                      </div>

                      <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                        <button
                          type="button"
                          onClick={async () => {
                            setRecommendedOptionId(o.optionId)
                            await writeNotepad(lockedOptions, o.optionId)
                          }}
                          style={{
                            border: "1px solid rgba(0,0,0,0.15)",
                            background: "#fff",
                            borderRadius: 12,
                            padding: "8px 10px",
                            fontWeight: 950,
                            cursor: "pointer",
                            flex: "1 1 140px",
                          }}
                        >
                          Set Recommended
                        </button>

                        <button
                          type="button"
                          onClick={async () => {
                            const next = lockedOptions.filter((x) => x.optionId !== o.optionId)
                            const nextRec = nextRecommendedOptionId(next, recommendedOptionId)
                            setLockedOptions(next)
                            setRecommendedOptionId(nextRec)
                            await writeNotepad(next, nextRec)
                          }}
                          style={{
                            border: "1px solid rgba(0,0,0,0.15)",
                            background: "#fff",
                            borderRadius: 12,
                            padding: "8px 10px",
                            fontWeight: 950,
                            cursor: "pointer",
                            flex: "1 1 110px",
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )
            })}
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 14 }}>
          <span style={page.pill}>Type: {txnType}</span>
          <span style={page.pill}>Capacity: {Money0.format(capacity)}</span>
          <span style={page.pill}>LVR tier: {lvrTier || "—"}</span>
        </div>
      </div>

      <ChooseLoanModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        products={products}
        borrowingCapacity={capacity}
        occupancy={occupancy}
        onChangeOccupancy={setOccupancy}
        amount={requestedAmount}
        onChangeAmount={setRequestedAmount}
        lvr={requestedLvr}
        onChangeLvr={(v) => setRequestedLvr(clamp(v, 1, 95))}
        rateTypeKey={rateTypeKey}
        onChangeRateTypeKey={setRateTypeKey}
        fixedTermKey={fixedTermKey}
        onChangeFixedTermKey={setFixedTermKey}
        lenderKey={lenderKey}
        onChangeLenderKey={setLenderKey}
        selectedProductId={selectedProductId}
        onSelectProduct={setSelectedProductId}
        lenderDirectory={lenderDirectory}
        onSendSms={handleSendSms}
        onLockSelection={handleLockSelection}
      />

      {(onBack || onNext) && (
        <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              style={{
                flex: 1,
                height: 56,
                borderRadius: 14,
                border: "1px solid rgba(0,0,0,0.12)",
                background: "#fff",
                fontWeight: 900,
                cursor: "pointer",
              }}
            >
              ← Back
            </button>
          )}
          {onNext && (
            <button
              type="button"
              onClick={onNext}
              style={{
                flex: 1,
                height: 56,
                borderRadius: 14,
                border: "1px solid rgba(0,0,0,0.12)",
                background: "#fff",
                fontWeight: 900,
                cursor: "pointer",
              }}
            >
              Next →
            </button>
          )}
        </div>
      )}
    </div>
  )
}

export default BorrowingCapacityStep